import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import { userProfilesQuery } from '@features/crm-customer-directory/api/user-profiles';
import { useNumberedSelector } from './use-numbered-selector';

import {
  crmCustomerLearningCertificationsQuery,
  crmCustomerLearningCertificationTypesQuery,
  crmCustomerLearningCourseStatusesQuery,
  crmCustomerLearningCourseSelectorQuery,
  crmCustomerLearningCourseTypesQuery,
  crmCustomerLearningCoursesQuery,
  crmCustomerLearningEmLogsQuery,
  crmCustomerLearningEmLogResultsQuery,
  crmCustomerLearningEmLogTypesQuery,
  crmCustomerLearningRegistrationPaymentStatusesQuery,
  crmCustomerLearningRegistrationReferenceTypesQuery,
  crmCustomerLearningRegistrationSelectorPageQuery,
  crmCustomerLearningRegistrationTypesQuery,
  crmCustomerLearningOrganizationsQuery,
  crmCustomerLearningKeys,
  crmCustomerLearningRegistrationsQuery,
  type CrmCustomerLearningQueryParams,
} from './queries';

const queryDefaults = {
  gcTime: 5 * 60 * 1000,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  retry: false,
  retryOnMount: false,
  staleTime: 30_000,
} as const;

/** Retain search-failure evidence only within the same authorized profile and locale. */
function useScopedRetainedData<T>(scope: string, data: T | undefined) {
  const retained = useRef<{ scope: string; data: T } | undefined>(undefined);
  useEffect(() => {
    if (data !== undefined) retained.current = { scope, data };
  }, [data, scope]);
  return retained.current?.scope === scope ? retained.current.data : undefined;
}

export type CrmCustomerLearningQueryOptions = {
  courseQuery?: string;
  filterControlsOpen?: boolean;
  instructorQuery?: string;
  registrationQuery?: string;
};

/**
 * Numbered-page Registration selector for the EM Log filter. It intentionally
 * owns only selector search state: collection filters and table search never
 * become selector facets.
 */
function useCrmCustomerLearningRegistrationSelector(
  params: Pick<CrmCustomerLearningQueryParams, 'customerId' | 'q' | 'viewerAccessScope'>,
  enabled: boolean
) {
  return useNumberedSelector({
    queryKey: crmCustomerLearningKeys.registrationSelector(params),
    pageQuery: (page) => crmCustomerLearningRegistrationSelectorPageQuery({ ...params, page }),
    itemKey: (registration) => registration.id,
    enabled: enabled && params.customerId.length > 0 && params.viewerAccessScope.length > 0,
  });
}

/**
 * The mounted Learning view reads each entity-owned canonical resource
 * directly. EM Logs use the same direct collection contract and independent
 * catalogue requests; collection filters never leak into selector requests.
 */
export function useCrmCustomerLearning(
  params: CrmCustomerLearningQueryParams,
  options: CrmCustomerLearningQueryOptions = {}
) {
  const collectionEnabled =
    params.viewerAccessScope.length > 0 && params.customerId.length > 0 && params.page > 0;
  const filterControlsEnabled = collectionEnabled && options.filterControlsOpen === true;
  const instructorSelectorEnabled = filterControlsEnabled;

  const emLogsQuery = useQuery({
    ...crmCustomerLearningEmLogsQuery(params),
    ...queryDefaults,
    enabled: collectionEnabled && params.section === 'attempts',
  });
  const emLogResultsQuery = useQuery({
    ...crmCustomerLearningEmLogResultsQuery(params),
    ...queryDefaults,
    enabled: filterControlsEnabled && params.section === 'attempts',
  });
  const emLogTypesQuery = useQuery({
    ...crmCustomerLearningEmLogTypesQuery(params),
    ...queryDefaults,
    enabled: filterControlsEnabled && params.section === 'attempts',
  });
  const coursesQuery = useQuery({
    ...crmCustomerLearningCoursesQuery(params),
    ...queryDefaults,
    enabled: collectionEnabled && params.section === 'courses',
  });
  const courseStatusesQuery = useQuery({
    ...crmCustomerLearningCourseStatusesQuery(params),
    ...queryDefaults,
    enabled: filterControlsEnabled && params.section === 'courses',
  });
  const courseTypesQuery = useQuery({
    ...crmCustomerLearningCourseTypesQuery(params),
    ...queryDefaults,
    enabled:
      filterControlsEnabled &&
      (params.section === 'attempts' ||
        params.section === 'courses' ||
        params.section === 'registrations'),
  });
  const courseSelectorParams = {
    customerId: params.customerId,
    locale: params.locale,
    q: options.courseQuery,
    viewerAccessScope: params.viewerAccessScope,
  };
  const courseSelectorQuery = useNumberedSelector({
    queryKey: crmCustomerLearningKeys.courseSelector(courseSelectorParams),
    pageQuery: (page) => crmCustomerLearningCourseSelectorQuery(courseSelectorParams, page),
    itemKey: (course) => course.courseRef,
    enabled: filterControlsEnabled && params.section === 'registrations',
  });
  const registrationsQuery = useQuery({
    ...crmCustomerLearningRegistrationsQuery(params),
    ...queryDefaults,
    enabled: collectionEnabled && params.section === 'registrations',
  });
  const registrationPaymentStatusesQuery = useQuery({
    ...crmCustomerLearningRegistrationPaymentStatusesQuery(params),
    ...queryDefaults,
    enabled: filterControlsEnabled && params.section === 'registrations',
  });
  const registrationReferenceTypesQuery = useQuery({
    ...crmCustomerLearningRegistrationReferenceTypesQuery(params),
    ...queryDefaults,
    enabled: filterControlsEnabled && params.section === 'registrations',
  });
  const registrationTypesQuery = useQuery({
    ...crmCustomerLearningRegistrationTypesQuery(params),
    ...queryDefaults,
    enabled: filterControlsEnabled && params.section === 'registrations',
  });
  const organizationsQuery = useQuery({
    ...crmCustomerLearningOrganizationsQuery(params),
    ...queryDefaults,
    enabled:
      filterControlsEnabled && (params.section === 'courses' || params.section === 'registrations'),
  });
  const certificationsQuery = useQuery({
    ...crmCustomerLearningCertificationsQuery(params),
    ...queryDefaults,
    enabled: collectionEnabled && params.section === 'certifications',
  });
  const certificationTypesQuery = useQuery({
    ...crmCustomerLearningCertificationTypesQuery(params),
    ...queryDefaults,
    enabled: filterControlsEnabled && params.section === 'certifications',
  });
  const instructorProfilesQuery = useNumberedSelector({
    queryKey: [
      ...crmCustomerLearningKeys.all,
      'instructor-selector',
      params.viewerAccessScope,
      params.customerId,
      options.instructorQuery ?? '',
    ],
    pageQuery: (page) =>
      userProfilesQuery(page, params.viewerAccessScope, options.instructorQuery, ['Instructor']),
    itemKey: (profile) => profile.id,
    enabled: instructorSelectorEnabled,
  });
  const emLogRegistrationSelector = useCrmCustomerLearningRegistrationSelector(
    {
      customerId: params.customerId,
      q: options.registrationQuery,
      viewerAccessScope: params.viewerAccessScope,
    },
    filterControlsEnabled && params.section === 'attempts'
  );

  const activeCollectionQuery =
    params.section === 'attempts'
      ? emLogsQuery
      : params.section === 'courses'
        ? coursesQuery
        : params.section === 'registrations'
          ? registrationsQuery
          : certificationsQuery;
  const activeSelectorQueries =
    params.section === 'attempts'
      ? [emLogResultsQuery, emLogTypesQuery]
      : params.section === 'courses'
        ? [courseStatusesQuery, courseTypesQuery, organizationsQuery]
        : params.section === 'registrations'
          ? [
              courseTypesQuery,
              courseSelectorQuery,
              registrationPaymentStatusesQuery,
              registrationReferenceTypesQuery,
              registrationTypesQuery,
              organizationsQuery,
            ]
          : [certificationTypesQuery];

  const retentionScope = JSON.stringify([
    params.customerId,
    params.viewerAccessScope,
    params.locale,
  ]);
  const retainedCourse = useScopedRetainedData(retentionScope, coursesQuery.data);
  const retainedRegistration = useScopedRetainedData(retentionScope, registrationsQuery.data);
  const retainedCertification = useScopedRetainedData(retentionScope, certificationsQuery.data);
  const retainedEmLogs = useScopedRetainedData(retentionScope, emLogsQuery.data);

  const emLogs =
    params.section === 'attempts' && emLogsQuery.data !== undefined
      ? emLogsQuery.data
      : params.section === 'attempts'
        ? retainedEmLogs
        : undefined;

  return {
    certificationCollection:
      params.section === 'certifications'
        ? (certificationsQuery.data ?? retainedCertification)
        : undefined,
    certificationTypes: certificationTypesQuery.data,
    courseCollection:
      params.section === 'courses' ? (coursesQuery.data ?? retainedCourse) : undefined,
    courseStatuses: courseStatusesQuery.data,
    courseSelector: courseSelectorQuery,
    courseTypes: courseTypesQuery.data,
    emLogs,
    emLogResults: emLogResultsQuery.data,
    emLogRegistrationSelector,
    emLogTypes: emLogTypesQuery.data,
    error: activeCollectionQuery.error,
    instructorProfiles: instructorProfilesQuery,
    selectors: {
      certificationTypes: certificationTypesQuery,
      courseStatuses: courseStatusesQuery,
      courseTypes: courseTypesQuery,
      emLogResults: emLogResultsQuery,
      emLogTypes: emLogTypesQuery,
      organizations: organizationsQuery,
      registrationPaymentStatuses: registrationPaymentStatusesQuery,
      registrationReferenceTypes: registrationReferenceTypesQuery,
      registrationTypes: registrationTypesQuery,
    },
    isError: activeCollectionQuery.isError,
    isFetching: activeCollectionQuery.isFetching,
    isLoading: activeCollectionQuery.isLoading,
    isPlaceholderData: false,
    isRetainedData:
      (params.section === 'courses' &&
        coursesQuery.data === undefined &&
        retainedCourse !== undefined) ||
      (params.section === 'registrations' &&
        registrationsQuery.data === undefined &&
        retainedRegistration !== undefined) ||
      (params.section === 'certifications' &&
        certificationsQuery.data === undefined &&
        retainedCertification !== undefined) ||
      (params.section === 'attempts' &&
        emLogsQuery.data === undefined &&
        retainedEmLogs !== undefined),
    isSuccess: activeCollectionQuery.isSuccess,
    organizations: organizationsQuery.data,
    refetch: async () =>
      await Promise.all([
        activeCollectionQuery.refetch(),
        ...activeSelectorQueries.map(async (query) => await query.refetch()),
      ]),
    registrationCollection:
      params.section === 'registrations'
        ? (registrationsQuery.data ?? retainedRegistration)
        : undefined,
    registrationPaymentStatuses: registrationPaymentStatusesQuery.data,
    registrationReferenceTypes: registrationReferenceTypesQuery.data,
    registrationTypes: registrationTypesQuery.data,
  };
}
